import { useState } from 'react'
import { useTypedSelector } from 'src/State/RootReducer'
import useShopSystemConfiguration from 'src/Libs/shop/hooks/use-shop-system-configuration'
import useShopSmartMeter from 'src/Libs/shop/hooks/use-shop-smart-meter'
import {
  BasicSystemResponseModel,
  Element,
  SmartMeterModalModelResponse,
  SystemConfigurationComponentsResponse,
  RetrofitBatteryModalResponse,
  Product,
} from 'src/Libs/shop/models'
import { useEffectShopAccess } from 'src/Libs/shop/hooks'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'
import { getImageUrlBySize } from 'src/Libs/shop/utils'
import { Component, ShippingDate } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationModel'
import {
  getBatteryPrices,
  getMainComponentPrices,
  getPowerMeterPrices,
  getSmartMeterPrices,
} from '../../../../utils/shop-system-configuration-price-calculation'

interface UseShopNormalSystemConfigurationCardReturn {
  basicData: BasicSystemResponseModel | null
  mainComponent: SystemConfigurationComponentsResponse | null
  selectedPowerMeters: Element[] | undefined
  selectedBattery: RetrofitBatteryModalResponse | null
  selectedSmartMeter: Product | undefined
  deliveryDate: ShippingDate | null
  selectedMainComponents: Component[] | null
  totalPrice: number
  discountedPrice: number
  basicSystemImageUrl: string | undefined
}

/*
 *  Handles data and price summary logic for the normal system configuration card
 */
export const useShopNormalSystemConfigurationCard = (): UseShopNormalSystemConfigurationCardReturn => {
  const configuratorItems = useTypedSelector(state => state.systemStorage.items)

  const deliveryDate = configuratorItems.length > 0 ? configuratorItems[0].desiredShippingDate : null
  const basicSystem = configuratorItems.length > 0 ? configuratorItems[0].configuration.configuratorId : null
  const selectedMainComponents = configuratorItems.length > 0 ? configuratorItems[0].configuration.components : null
  const powerMeters = configuratorItems.length > 0 ? configuratorItems[0].configuration.powerMeters : null
  const retrofitBattery = configuratorItems.length > 0 ? configuratorItems[0].configuration.retrofitBattery : null
  const selectedSmartMeterId = configuratorItems.length > 0 ? configuratorItems[0].configuration.smartMeter?.id : null

  const [basicData, setBasicData] = useState<BasicSystemResponseModel | null>(null)
  const [mainComponent, setMainComponent] = useState<SystemConfigurationComponentsResponse | null>(null)
  const [selectedPowerMeters, setSelectedPowerMeters] = useState<Element[] | undefined>()
  const [selectedBattery, setSelectedBattery] = useState<RetrofitBatteryModalResponse | null>(null)
  const [smartMeters, setSmartMeters] = useState<SmartMeterModalModelResponse | null>(null)
  const [totalPrice, setTotalPrice] = useState<number>(0)
  const [discountedPrice, setDiscountedPrice] = useState<number>(0)

  const { getSingleSystemConfiguration, getSystemConfigurationComponents, getShopPowerMeters, getRetrofitBattery } = useShopSystemConfiguration()
  const { getSmartMeters } = useShopSmartMeter()

  const selectedSmartMeter = smartMeters?.elements.find(smartMeter => smartMeter.id === selectedSmartMeterId)

  useEffectShopAccess(() => {
    if (basicSystem != null) {
      getSingleSystemConfiguration(basicSystem).then(result => setBasicData(result))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryDate, basicSystem])

  useEffectShopAccess(() => {
    if (basicSystem) {
      getSystemConfigurationComponents(basicSystem).then(result => setMainComponent(result))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMainComponents])

  useEffectShopAccess(() => {
    if (selectedSmartMeterId) {
      getSmartMeters().then(result => setSmartMeters(result))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSmartMeterId])

  useEffectShopAccess(() => {
    setSelectedPowerMeters([])

    const powerMetersArray = powerMeters?.reduce<string[]>((acc, item) => {
      if (item.powerMeterId !== null) {
        acc.push(item.powerMeterId)
      }
      if (item.powerConverterId && item.powerConverterId !== null) {
        acc.push(item.powerConverterId)
      }
      return acc
    }, [])

    powerMetersArray && powerMetersArray.length > 0 && getShopPowerMeters(powerMetersArray).then(result => setSelectedPowerMeters(result ?? []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [powerMeters])

  useEffectShopAccess(() => {
    setSelectedBattery(null)

    if (retrofitBattery !== null) {
      getRetrofitBattery(retrofitBattery.id).then(result => {
        setSelectedBattery(result)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retrofitBattery])

  useEffectShopAccess(() => {
    let normalSum = 0
    let discountedSum = 0

    const mainComponentPrices = getMainComponentPrices(mainComponent, selectedMainComponents)
    discountedSum += mainComponentPrices.price
    normalSum += mainComponentPrices.discountedPrice

    const powerMeterPrices = getPowerMeterPrices(selectedPowerMeters)
    discountedSum += powerMeterPrices.price
    normalSum += powerMeterPrices.discountedPrice

    const smartMeterPrices = getSmartMeterPrices(selectedSmartMeter)
    discountedSum += smartMeterPrices.price
    normalSum += smartMeterPrices.discountedPrice

    const batteryPrices = getBatteryPrices(selectedBattery, retrofitBattery)
    discountedSum += batteryPrices.price
    normalSum += batteryPrices.discountedPrice

    setTotalPrice(Number.parseFloat(discountedSum.toFixed(2)))
    setDiscountedPrice(Number.parseFloat(normalSum.toFixed(2)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainComponent, selectedMainComponents, selectedPowerMeters, selectedBattery, selectedSmartMeter, retrofitBattery, selectedSmartMeterId])
  const basicSystemImageUrl = getImageUrlBySize(basicData?.mainImage?.thumbnails, ShopImageThumnbailSizes.Small)

  return {
    basicData,
    deliveryDate,
    selectedMainComponents,
    mainComponent,
    selectedPowerMeters,
    selectedBattery,
    selectedSmartMeter,
    totalPrice,
    discountedPrice,
    basicSystemImageUrl,
  }
}
